import { Link } from "react-router-dom";
import type { Location } from "@/types";

export default function LocationsSection({
  locations,
  showAllLink = true,
  embedded = false,
}: {
  locations: Location[];
  showAllLink?: boolean;
  embedded?: boolean;
}) {
  return (
    <section className={embedded ? "w-full" : "mx-auto w-full max-w-[1400px] px-4 py-14 md:px-6 md:py-16"}>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <span className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-secondary-700">
            <span className="h-1 w-1 rounded-full bg-primary-500"></span>
            Explore by location
          </span>
          <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">
            Find businesses near you in the Mysore region
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            Pick a district or jump straight into a locality to see verified businesses around you.
          </p>
        </div>
        {showAllLink && (
          <Link
            to="/locations"
            className="group inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors duration-200 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800 cursor-pointer"
          >
            All locations
            <i className="ri-arrow-right-line text-base transition-transform duration-200 group-hover:translate-x-0.5"></i>
          </Link>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {locations.map((loc) => (
          <Link
            key={loc.id}
            to={`/location/${loc.slug}`}
            className="group relative block h-56 overflow-hidden rounded-lg cursor-pointer"
          >
            <img
              src={loc.image}
              alt={`${loc.name} businesses`}
              title={`${loc.name} business directory`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 p-4">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-bold text-foreground-950">
                <i className="ri-map-pin-2-fill"></i> {loc.district}
              </span>
              <h3 className="mt-2 font-heading text-lg font-extrabold text-background-50">{loc.name}</h3>
              <p className="mt-1 text-xs text-background-200">{loc.businessCount.toLocaleString()} listings</p>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {locations.map((loc) => (
          <Link
            key={`pill-${loc.id}`}
            to={`/location/${loc.slug}`}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-800 cursor-pointer"
          >
            <i className="ri-map-pin-line text-secondary-500"></i>
            {loc.name}
          </Link>
        ))}
      </div>
    </section>
  );
}